import { useState } from 'react';
import type { AutomationRunItem, AutomationRunStatus } from '@hygge/shared';
import { useAutomationRuns } from '@/hooks/use-automations';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

// ─── Status pill ────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<AutomationRunStatus, 'success' | 'warning' | 'destructive'> = {
  success: 'success',
  partial: 'warning',
  failed: 'destructive',
};

const STATUS_LABEL: Record<AutomationRunStatus, string> = {
  success: 'Success',
  partial: 'Partial',
  failed: 'Failed',
};

// ─── Duration formatting ─────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ─── Detail rendering ────────────────────────────────────────────────────────

interface ActionDetail {
  ok: boolean;
  type?: string;
  error?: string;
  [key: string]: unknown;
}

function isActionDetailArray(v: unknown): v is ActionDetail[] {
  return (
    Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && v[0] !== null && 'ok' in v[0]
  );
}

function RunDetail({ detail }: { detail: unknown }) {
  if (isActionDetailArray(detail)) {
    return (
      <div className="space-y-1.5 pt-1">
        {detail.map((d, i) => (
          <div
            key={i}
            className={cn(
              'flex items-start gap-2 rounded-md px-2 py-1.5 text-xs',
              d.ok ? 'bg-hygge/5 text-foreground' : 'bg-destructive/5 text-destructive'
            )}
          >
            <span className="mt-px shrink-0 font-mono text-[10px]">{d.ok ? '✓' : '✗'}</span>
            <div className="min-w-0">
              {d.type && <span className="font-medium">{d.type}</span>}
              {d.error && <span className="ml-1.5 text-destructive">{d.error}</span>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Fallback: pretty-print as JSON
  return (
    <pre className="mt-1 overflow-x-auto rounded-md bg-muted px-3 py-2 text-[10px] text-muted-foreground">
      {JSON.stringify(detail, null, 2)}
    </pre>
  );
}

// ─── Single run row ──────────────────────────────────────────────────────────

function RunRow({ run }: { run: AutomationRunItem }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = run.detail != null;
  const ChevronIcon = expanded ? ChevronDown : ChevronRight;
  const detailId = `run-detail-${run.id}`;

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => hasDetail && setExpanded((v) => !v)}
        aria-expanded={hasDetail ? expanded : undefined}
        aria-controls={hasDetail ? detailId : undefined}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          hasDetail ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'
        )}
      >
        {/* Expand chevron */}
        {hasDetail ? (
          <ChevronIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <span className="h-3 w-3 shrink-0" />
        )}

        {/* Status pill */}
        <Badge variant={STATUS_VARIANT[run.status]}>{STATUS_LABEL[run.status]}</Badge>

        {/* Trigger event */}
        <span className="font-mono text-[10px] text-muted-foreground">{run.triggerEvent}</span>

        {/* Actions ok/total */}
        <span className="text-muted-foreground">
          {run.actionsOk}/{run.actionsTotal} actions
        </span>

        {/* Duration */}
        {run.durationMs != null && (
          <span className="text-muted-foreground">{formatDuration(run.durationMs)}</span>
        )}

        {/* Error summary */}
        {run.error && <span className="max-w-[180px] truncate text-destructive">{run.error}</span>}

        {/* Relative time — right-aligned */}
        <span className="ml-auto shrink-0 text-muted-foreground">
          {formatDistanceToNow(new Date(run.createdAt), { addSuffix: true })}
        </span>
      </button>

      {/* Expanded detail */}
      {expanded && hasDetail && (
        <div id={detailId} className="border-t border-border px-3 pb-3">
          <RunDetail detail={run.detail} />
        </div>
      )}
    </div>
  );
}

// ─── Run log ─────────────────────────────────────────────────────────────────

export function RunLog({ automationId }: { automationId: string }) {
  const { data: runs = [], isLoading } = useAutomationRuns(automationId);

  if (isLoading) return <p className="text-xs text-muted-foreground">Loading runs…</p>;
  if (runs.length === 0) return <p className="text-xs text-muted-foreground">No runs yet.</p>;

  return (
    <div className="space-y-1.5">
      {runs.map((run) => (
        <RunRow key={run.id} run={run} />
      ))}
    </div>
  );
}
